import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'

const DEFAULT_FORM_PROVIDER = 'FormEvo'

const initialState = {
  mode: 'create', // 'create' | 'edit'
  matterDocumentId: null,
  selectedMatter: null,
  formProvider: DEFAULT_FORM_PROVIDER,
  selectedFormId: null,
  mappedFormId: null,
  reviewData: []
}

// Build the option shape MatterSelect expects from a matter document row
const deriveMatterFromFormData = (formData) => {
  if (!formData) return null

  const { clientNo, matterNo, clientMatterNo, matterDescription } = formData
  if (clientNo == null || matterNo == null) return null

  const resolvedClientMatterNo = clientMatterNo || `${clientNo}-${matterNo}`

  return {
    clientNo,
    matterNo,
    clientMatterNo: resolvedClientMatterNo,
    matterDescription: matterDescription ?? '',
    searchTerm: resolvedClientMatterNo
  }
}

// Combine the three read-endpoint responses into one row per FormEvo form field
// (technical-spec.md's row-building algorithm, implemented literally). A mapping rule
// with no corresponding entry in `values` is "mapped but currently blank", never
// "unmapped" -- MappedFormFieldValueGetter omits rows it cannot resolve rather than
// sending a null placeholder.
export const buildReviewDataRows = ({ formFields, mappingRules, values }) => {
  const valueByMappedFormFieldId = new Map((values ?? []).map((value) => [value.mappedFormFieldId, value.value]))
  const ruleByFormFieldId = new Map((mappingRules ?? []).map((rule) => [rule.formFieldId, rule]))

  return (formFields ?? []).map((field) => {
    const rule = ruleByFormFieldId.get(field.formFieldId)
    const isMapped = rule !== undefined
    const resolvedValue = isMapped ? valueByMappedFormFieldId.get(rule.mappedFormFieldId) : undefined
    const isReadOnly = Boolean(field.isReadOnly || field.isAutoCompleted)

    return {
      formFieldId: field.formFieldId,
      formFieldName: field.name,
      formFieldDescription: field.description,
      isReadOnly,
      isMapped,
      mappedFormFieldId: rule?.mappedFormFieldId ?? null,
      resolvedValue: resolvedValue ?? null,
      // Additive display fields, not part of the literal algorithm: what to show in the
      // left ("source Osprey field") column when a row is mapped.
      mappingType: rule?.mappingType ?? null,
      sourceFieldLabel: rule ? rule.fieldDescription || rule.customDataPageDescription || rule.mappingType : null,
      send: isReadOnly ? false : isMapped
    }
  })
}

export const useThirdPartyFormWizardStore = storeWrapper('useThirdPartyFormWizardStore', (set, get) => ({
  ...initialState,
  updateState: ({ key, value }) => {
    set({ [key]: value })
  },
  // Seed the store at launch so the wizard steps are purely store-driven.
  // create: pass the current matter as selectedMatter.
  // edit: pass the form's row as formData (and optionally selectedMatter).
  initialize: ({ mode = 'create', selectedMatter = null, formData = null } = {}) => {
    set({
      ...initialState,
      mode,
      matterDocumentId: formData?.matterDocumentId ?? null,
      selectedMatter: selectedMatter ?? deriveMatterFromFormData(formData),
      formProvider: formData?.formProviderName || DEFAULT_FORM_PROVIDER,
      selectedFormId: formData?.formId ?? null
    })
  },
  // OS-2698's real, accepted contract (PR 9510): resolve the mappedFormId this screen
  // needs from the formId picked in the prior step. totalRowCount is 0 or 1 -- MappedForm
  // carries no worktype-scoping column, so at most one row exists per formId.
  getMappedFormId: async ({ formId }) => {
    const response = await ospreyAPI({
      method: 'POST',
      service: 'matters',
      endpoint: 'MappedForm/GetAllExtendedBySearchCriteria',
      data: {
        formName: null,
        formDescription: null,
        workTypeId: null,
        formId,
        startAtIndex: 0,
        pageSize: 1
      }
    })

    return response?.rows?.[0]?.mappedFormId ?? null
  },
  getFormFieldsByFormId: async ({ formId }) =>
    ospreyAPI({
      method: 'GET',
      service: 'matters',
      endpoint: 'FormField/GetAllByFormId',
      data: { formId }
    }),
  getMappedFormFieldsByMappedFormId: async ({ mappedFormId }) =>
    ospreyAPI({
      method: 'GET',
      service: 'matters',
      endpoint: 'MappedFormField/GetAllExtendedByMappedFormId',
      data: { mappedFormId }
    }),
  getMappedFormFieldValues: async ({ clientNo, matterNo, mappedFormId }) =>
    ospreyAPI({
      method: 'GET',
      service: 'matters',
      endpoint: 'MappedFormField/GetAllMappedFormFieldValues',
      data: { clientNo, matterNo, mappedFormId }
    }),
  // Resolves mappedFormId, then combines the three read endpoints into review-data rows.
  // mappedFormId === null means this form has never been mapped -- the caller shows the
  // "not yet configured for field mapping" state and blocks continuing.
  fetchReviewDataRows: async ({ formId, clientNo, matterNo }) => {
    const mappedFormId = await get().getMappedFormId({ formId })
    set({ mappedFormId })

    if (mappedFormId == null) return { mappedFormId: null, rows: [] }

    const mappingRules = await get().getMappedFormFieldsByMappedFormId({ mappedFormId })
    // A MappedForm row with zero field rows is an abandoned-wizard artefact (OS-2722's
    // idempotent Create fires on MapFieldsStep's mount, before Save) -- treat it as not
    // configured, the same as mappedFormId == null, rather than building an all-empty
    // review-data set and letting OS-2362's Confirmation step submit a near-empty document.
    if (!mappingRules || mappingRules.length === 0) return { mappedFormId: null, rows: [] }

    const [formFields, values] = await Promise.all([
      get().getFormFieldsByFormId({ formId }),
      get().getMappedFormFieldValues({ clientNo, matterNo, mappedFormId })
    ])

    return { mappedFormId, rows: buildReviewDataRows({ formFields, mappingRules, values }) }
  },
  createDocument: async ({ mappedFormId, clientNo, matterNo, mappedFormFieldValues }) =>
    ospreyAPI({
      method: 'POST',
      service: 'matters',
      endpoint: `MappedForm/CreateDocument?mappedFormId=${mappedFormId}&clientNo=${encodeURIComponent(clientNo)}&matterNo=${matterNo}`,
      data: mappedFormFieldValues
    }),
  reset: () => set(initialState)
}))
